const PREVIEW_MODE_STORAGE_KEY = "chopshop.preview.mode";
const PREVIEW_EVENT_STORAGE_KEY = "chopshop.preview.events";

// The flag lives in the browser, so it names the one account it belongs to:
// set unowned at signup, claimed by the first signed-in account that reads it.
// Any other account on the same browser never sees preview mode.
type PreviewModeState = {
  active: boolean;
  source: string;
  activatedAt: string;
  userId?: string;
  claimable?: boolean;
};

function getSafeWindow(): Window | null {
  return typeof window === "undefined" ? null : window;
}

export function activatePreviewMode(source = "free_preview"): void {
  const win = getSafeWindow();
  if (!win) return;

  const state: PreviewModeState = {
    active: true,
    source,
    activatedAt: new Date().toISOString(),
    claimable: true,
  };

  win.localStorage.setItem(PREVIEW_MODE_STORAGE_KEY, JSON.stringify(state));
}

export function getPreviewModeState(): PreviewModeState | null {
  const win = getSafeWindow();
  if (!win) return null;

  try {
    const raw = win.localStorage.getItem(PREVIEW_MODE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PreviewModeState>;
    if (!parsed || parsed.active !== true) return null;
    return {
      active: true,
      source: typeof parsed.source === "string" && parsed.source.trim() ? parsed.source.trim() : "free_preview",
      activatedAt:
        typeof parsed.activatedAt === "string" && parsed.activatedAt.trim()
          ? parsed.activatedAt.trim()
          : new Date().toISOString(),
      userId: typeof parsed.userId === "string" && parsed.userId ? parsed.userId : undefined,
      claimable: parsed.claimable === true,
    };
  } catch {
    return null;
  }
}

export function isPreviewModeActive(userId?: string | null): boolean {
  const state = getPreviewModeState();
  if (!state || !userId) return false;
  if (state.userId) return state.userId === userId;
  if (!state.claimable) {
    // Written before flags were owned: nobody can tell whose it was, so drop it.
    clearPreviewMode();
    return false;
  }
  const win = getSafeWindow();
  if (!win) return false;
  win.localStorage.setItem(
    PREVIEW_MODE_STORAGE_KEY,
    JSON.stringify({ active: true, source: state.source, activatedAt: state.activatedAt, userId }),
  );
  return true;
}

export function clearPreviewMode(): void {
  const win = getSafeWindow();
  if (!win) return;
  win.localStorage.removeItem(PREVIEW_MODE_STORAGE_KEY);
  win.sessionStorage.removeItem(PREVIEW_EVENT_STORAGE_KEY);
}

function getPreviewEvents(): string[] {
  const win = getSafeWindow();
  if (!win) return [];

  try {
    const raw = win.sessionStorage.getItem(PREVIEW_EVENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    return [];
  }
}

export function hasTrackedPreviewEvent(eventName: string): boolean {
  return getPreviewEvents().includes(eventName);
}

export function markPreviewEvent(eventName: string): void {
  const win = getSafeWindow();
  if (!win || !eventName.trim()) return;
  const next = Array.from(new Set([...getPreviewEvents(), eventName.trim()]));
  win.sessionStorage.setItem(PREVIEW_EVENT_STORAGE_KEY, JSON.stringify(next));
}
